import React from "react";
import { Link } from "react-router-dom";
import CandidatePageShell from "./CandidatePageShell";
import JobCard from "../Jobs/JobCard";
import useFetch from "../../useFetch";
import { getStoredUser } from "../../auth";
import { formatDate, mapApiJob } from "../../utils/jobFormatters";
import AccountReferralCard from "../Common/AccountReferralCard";
import "./Candidates.css";

const CandidateDashboard = () => {
  const user = getStoredUser();
  const { response: applicationsResponse, isLoading: isLoadingApplications } = useFetch(process.env.REACT_APP_BASE_API_URL + "/candidate/applications?per_page=4");
  const { response: savedResponse } = useFetch(process.env.REACT_APP_BASE_API_URL + "/candidate/saved-jobs?per_page=4");
  const { response: alertsResponse } = useFetch(process.env.REACT_APP_BASE_API_URL + "/candidate/job-alerts");
  const { response: jobsResponse, isLoading: isLoadingJobs } = useFetch(process.env.REACT_APP_BASE_API_URL + "/jobs?per_page=4");
  const { response: profileResponse } = useFetch(process.env.REACT_APP_BASE_API_URL + "/candidate-profile");

  const applications = applicationsResponse?.applications?.data ?? [];
  const latestJobs = jobsResponse?.jobs?.data ?? [];
  const applicationTotal = applicationsResponse?.applications?.meta?.total ?? 0;
  const savedTotal = savedResponse?.saved_jobs?.meta?.total ?? 0;
  const alertTotal = alertsResponse?.job_alerts?.length ?? 0;
  const profileCompletion = profileResponse?.profile_completion ?? {};
  const missingProfileSections = profileCompletion?.missing_sections ?? [];
  const account = profileResponse?.account ?? user;

  const stats = [
    { id: "jobs-applied", value: applicationTotal, label: "Jobs Applied", icon: "flaticon-briefcase" },
    { id: "shortlisted", value: savedTotal, label: "Saved Jobs", icon: "la la-bookmark-o", className: "ui-green" },
    { id: "new-matches", value: alertTotal, label: "Job Alerts", icon: "la la-bell", className: "ui-yellow" },
  ];

  const nextSteps = [
    { id: "complete-profile", label: "Complete your profile details", to: "/candidate-profile" },
    { id: "browse-jobs", label: "Browse active job listings", to: "/jobs" },
    { id: "set-alerts", label: "Create alerts for preferred job roles", to: "/candidate-job-alerts" },
  ];

  const applicationStatusLabel = (application) => {
    if (application?.submission_status === "pending_verification") {
      return "pending email verification";
    }

    return application?.status;
  };

  const renderApplicationJob = (application) => {
    const mappedJob = mapApiJob(application.job, { time: `Applied ${formatDate(application.created_at)}` });

    return mappedJob ? (
      <JobCard job={mappedJob} />
    ) : (
      <div className="employee-empty-state">This job is no longer available.</div>
    );
  };

  return (
    <CandidatePageShell
      active="dashboard"
      title={`Welcome back, ${user?.name ?? "Candidate"}`}
      text="A quick overview of your job search activity."
    >
      <div className="row">
        {stats.map((item) => (
          <div className="col-lg-4 col-md-6 col-sm-12" key={item.id}>
            <div className={`ui-item ${item.className || ""}`}>
              <div className="left">
                <i className={`icon ${item.icon}`}></i>
              </div>
              <div className="right">
                <h4>{item.value}</h4>
                <p>{item.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row">
        <div className="col-lg-8">
          <div className="applicants-widget ls-widget">
            <div className="widget-title">
              <h4>Latest Jobs Posted</h4>
              <Link to="/jobs" className="theme-btn btn-style-three">View All Jobs</Link>
            </div>
            <div className="widget-content">
              {isLoadingJobs && <h5 className="text-center my-3">Loading Data</h5>}
              <div className="candidate-simple-list">
                {!isLoadingJobs && latestJobs.map((job) => {
                  const mappedJob = mapApiJob(job);

                  if (!mappedJob) return null;

                  return (
                    <Link to={`/jobs/${job.id}`} className="candidate-list-item" key={job.id}>
                      <span className="company-logo">
                        {mappedJob.logo ? <img src={mappedJob.logo} alt={mappedJob.company} /> : <span className="company-logo-initials">{mappedJob.company.charAt(0)}</span>}
                      </span>
                      <span className="candidate-list-content">
                        <strong>{mappedJob.title}</strong>
                        <span>{mappedJob.company} - {mappedJob.location}</span>
                      </span>
                      <span className="candidate-list-tag">{mappedJob.type}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="notification-widget ls-widget candidate-account-widget">
            <div className="widget-title">
              <h4>Account Referral</h4>
            </div>
            <div className="widget-content">
              <AccountReferralCard account={account} />
            </div>
          </div>

          <div className="notification-widget ls-widget candidate-completion-widget">
            <div className="widget-title">
              <h4>Profile Strength</h4>
              <Link to="/candidate-profile" className="theme-btn btn-style-three">Edit</Link>
            </div>
            <div className="widget-content">
              <div className="dashboard-completion-card">
                <div className="dashboard-completion-head">
                  <strong>{profileCompletion?.percentage ?? 0}%</strong>
                  <span>{profileCompletion?.completed ?? 0} of {profileCompletion?.total ?? 0} sections complete</span>
                </div>
                <div className="dashboard-completion-bar">
                  <span style={{ width: `${profileCompletion?.percentage ?? 0}%` }}></span>
                </div>
                {missingProfileSections.length > 0 ? (
                  <>
                    <p>Missing details</p>
                    <ul>
                      {missingProfileSections.slice(0, 5).map((section) => (
                        <li key={section.key}>{section.label}</li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p>Your profile has the core details employers expect.</p>
                )}
              </div>
            </div>
          </div>

          <div className="notification-widget ls-widget">
            <div className="widget-title">
              <h4>Next Steps</h4>
            </div>
            <div className="widget-content">
              <ul className="candidate-task-list">
                {nextSteps.map((item) => (
                  <li key={item.id}>
                    <span className="la la-check"></span>
                    <Link to={item.to}>{item.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="col-lg-12">
          <div className="applicants-widget ls-widget">
            <div className="widget-title">
              <h4>Recently Applied Jobs</h4>
              <Link to="/candidate-applied-jobs" className="theme-btn btn-style-three">View History</Link>
            </div>
            <div className="widget-content">
              {isLoadingApplications && <h5 className="text-center my-3">Loading Data</h5>}
              <div className="row">
                {!isLoadingApplications && applications.map((application) => (
                  <div className="col-lg-6 col-md-12 col-sm-12 candidate-job-card" key={application.id}>
                    {renderApplicationJob(application)}
                    <div className="candidate-job-status">
                      <span className={application.status === "shortlisted" ? "success" : ""}>{applicationStatusLabel(application)}</span>
                      <span>{application.resume_original_name ?? "Resume attached"}</span>
                    </div>
                  </div>
                ))}
              </div>

              {!isLoadingApplications && applications.length === 0 && (
                <div className="employee-empty-state">No applications yet. Browse jobs and apply when you find a good fit.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </CandidatePageShell>
  );
};

export default CandidateDashboard;
